import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAdmin } from '../../context/AdminContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { useToast } from '../../hooks/use-toast';
import { Palette, Type, Save } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Appearance = () => {
  const { getAuthHeaders } = useAdmin();
  const { toast } = useToast();
  const [settings, setSettings] = useState({
    primary_color: '#cc8b3a',
    secondary_color: '#d4af37',
    background_color: '#1a1512',
    text_color: '#ffffff',
    heading_font: 'Playfair Display',
    body_font: 'Inter'
  });

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const response = await axios.get(`${API}/admin/settings`, {
        headers: getAuthHeaders()
      });
      setSettings(response.data);
    } catch (error) {
      console.error('Erro ao carregar configurações:', error);
    }
  };

  const handleColorChange = (field, value) => {
    setSettings({ ...settings, [field]: value });
  };

  const handleSaveColors = async () => {
    try {
      await axios.patch(
        `${API}/admin/settings/colors`,
        {
          primary_color: settings.primary_color,
          secondary_color: settings.secondary_color,
          background_color: settings.background_color,
          text_color: settings.text_color
        },
        { headers: getAuthHeaders() }
      );
      toast({ title: 'Cores atualizadas com sucesso!' });
    } catch (error) {
      toast({
        title: 'Erro',
        description: 'Erro ao atualizar cores',
        variant: 'destructive'
      });
    }
  };

  const handleSaveFonts = async () => {
    try {
      await axios.patch(
        `${API}/admin/settings/fonts`,
        {
          heading_font: settings.heading_font,
          body_font: settings.body_font
        },
        { headers: getAuthHeaders() }
      );
      toast({ title: 'Fontes atualizadas com sucesso!' });
    } catch (error) {
      toast({
        title: 'Erro',
        description: 'Erro ao atualizar fontes',
        variant: 'destructive'
      });
    }
  };

  const googleFonts = [
    'Inter',
    'Playfair Display',
    'Roboto',
    'Open Sans',
    'Lato',
    'Montserrat',
    'Oswald',
    'Raleway',
    'Poppins',
    'Merriweather'
  ];

  return (
    <div className="p-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-white mb-2">Aparência</h1>
        <p className="text-white/60">Personalize cores e tipografia do site</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Cores */}
        <Card className="bg-gradient-to-br from-[#3a2f24] to-[#4a3f2e] border-[#cc8b3a]/20">
          <CardHeader>
            <CardTitle className="text-white flex items-center">
              <Palette className="w-5 h-5 mr-2 text-[#cc8b3a]" />
              Cores
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label className="text-white">Cor Primária</Label>
              <div className="flex space-x-2">
                <Input
                  type="color"
                  value={settings.primary_color}
                  onChange={(e) => handleColorChange('primary_color', e.target.value)}
                  className="w-20 h-10"
                />
                <Input
                  type="text"
                  value={settings.primary_color}
                  onChange={(e) => handleColorChange('primary_color', e.target.value)}
                  className="flex-1 bg-black border-[#cc8b3a]/30 text-white"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-white">Cor Secundária</Label>
              <div className="flex space-x-2">
                <Input
                  type="color"
                  value={settings.secondary_color}
                  onChange={(e) => handleColorChange('secondary_color', e.target.value)}
                  className="w-20 h-10"
                />
                <Input
                  type="text"
                  value={settings.secondary_color}
                  onChange={(e) => handleColorChange('secondary_color', e.target.value)}
                  className="flex-1 bg-black border-[#cc8b3a]/30 text-white"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-white">Cor de Fundo</Label>
              <div className="flex space-x-2">
                <Input
                  type="color"
                  value={settings.background_color}
                  onChange={(e) => handleColorChange('background_color', e.target.value)}
                  className="w-20 h-10"
                />
                <Input
                  type="text"
                  value={settings.background_color}
                  onChange={(e) => handleColorChange('background_color', e.target.value)}
                  className="flex-1 bg-black border-[#cc8b3a]/30 text-white"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-white">Cor do Texto</Label>
              <div className="flex space-x-2">
                <Input
                  type="color"
                  value={settings.text_color}
                  onChange={(e) => handleColorChange('text_color', e.target.value)}
                  className="w-20 h-10"
                />
                <Input
                  type="text"
                  value={settings.text_color}
                  onChange={(e) => handleColorChange('text_color', e.target.value)}
                  className="flex-1 bg-black border-[#cc8b3a]/30 text-white"
                />
              </div>
            </div>

            <Button
              onClick={handleSaveColors}
              className="w-full bg-[#cc8b3a] hover:bg-[#d4af37] text-black font-bold"
            >
              <Save className="w-4 h-4 mr-2" />
              Salvar Cores
            </Button>
          </CardContent>
        </Card>

        {/* Tipografia */}
        <Card className="bg-gradient-to-br from-[#3a2f24] to-[#4a3f2e] border-[#cc8b3a]/20">
          <CardHeader>
            <CardTitle className="text-white flex items-center">
              <Type className="w-5 h-5 mr-2 text-[#cc8b3a]" />
              Tipografia
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label className="text-white">Fonte dos Títulos</Label>
              <select
                value={settings.heading_font}
                onChange={(e) => setSettings({ ...settings, heading_font: e.target.value })}
                className="w-full p-2 rounded-md bg-black border border-[#cc8b3a]/30 text-white"
              >
                {googleFonts.map((font) => (
                  <option key={font} value={font}>
                    {font}
                  </option>
                ))}
              </select>
              <div
                className="p-4 bg-black rounded-md text-white text-2xl"
                style={{ fontFamily: settings.heading_font }}
              >
                Exemplo de Título
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-white">Fonte do Corpo</Label>
              <select
                value={settings.body_font}
                onChange={(e) => setSettings({ ...settings, body_font: e.target.value })}
                className="w-full p-2 rounded-md bg-black border border-[#cc8b3a]/30 text-white"
              >
                {googleFonts.map((font) => (
                  <option key={font} value={font}>
                    {font}
                  </option>
                ))}
              </select>
              <div
                className="p-4 bg-black rounded-md text-white"
                style={{ fontFamily: settings.body_font }}
              >
                Este é um exemplo de texto no corpo do site. A fonte selecionada será
                aplicada a todos os parágrafos e textos gerais.
              </div>
            </div>

            <Button
              onClick={handleSaveFonts}
              className="w-full bg-[#cc8b3a] hover:bg-[#d4af37] text-black font-bold"
            >
              <Save className="w-4 h-4 mr-2" />
              Salvar Fontes
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Appearance;
